import { useState, useMemo } from 'react';
import {
  IncidentCategoryOption,
  OrderDirectionOption,
  PageSizeOption,
} from '@/graphql/generated';
import {
  useContractDetailsContext,
  useUserLanguage,
  useAddExpenseFormModalContext,
} from '@/customHooks';
import { incidentCategoryMapper, orderDirectionMapper } from '@/helpers';
import { Button, LoadingSpinner } from '../atoms';
import { NormalFormSelectionField, Pagination } from '../molecules';
import textsData from '@/textData/ContractDetailTexts.json';

type LocalFilters = {
  categoryOption: IncidentCategoryOption | null;
  orderDirection: OrderDirectionOption;
};

const INITIAL_FILTERS: LocalFilters = {
  categoryOption: null,
  orderDirection: OrderDirectionOption.Desc,
};

const readableDate = (date: Date | string | null | undefined, userLanguage: 'es' | 'en') => {
  if (!date) return '-';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString(userLanguage === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return '-';
  }
};

const formatAmount = (amount: string, currency: string, userLanguage: 'es' | 'en') => {
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return amount;
  return new Intl.NumberFormat(userLanguage === 'es' ? 'es-ES' : 'en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  }).format(numAmount);
};

export const ContractDetailExpensesList = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es.expensesList : textsData.en.expensesList;

  const { contract, expensesReport } = useContractDetailsContext();
  const { openModal } = useAddExpenseFormModalContext();

  const [filters, setFilters] = useState<LocalFilters>(INITIAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  const allExpenses = useMemo(() => expensesReport?.items ?? [], [expensesReport]);

  const filteredAndSortedExpenses = useMemo(() => {
    const filtered = allExpenses.filter((expense) => {
      if (!filters.categoryOption) return true;
      return expense.typeOption === filters.categoryOption;
    });

    return [...filtered].sort((a, b) => {
      const aDate = new Date(a.date).getTime();
      const bDate = new Date(b.date).getTime();
      return filters.orderDirection === OrderDirectionOption.Asc ? aDate - bDate : bDate - aDate;
    });
  }, [allExpenses, filters.categoryOption, filters.orderDirection]);

  const flattenedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredAndSortedExpenses.slice(start, end).map((expense) => ({
      id: expense.id,
      category: incidentCategoryMapper(expense.typeOption, userLanguage),
      title: expense.title || '-',
      description: expense.description || '-',
      date: expense.date,
      amount: formatAmount(expense.expenseCost, expense.currencyOption, userLanguage),
    }));
  }, [currentPage, filteredAndSortedExpenses, userLanguage]);

  const categoryOptions = Object.values(IncidentCategoryOption).map((value) => ({
    name: incidentCategoryMapper(value, userLanguage),
    value,
  }));

  const orderDirectionOptions = Object.values(OrderDirectionOption).map((value) => ({
    name: orderDirectionMapper(value, userLanguage),
    value,
  }));

  const handleFilterChange = (field: keyof LocalFilters, value: string | null) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const currentDataCount = filteredAndSortedExpenses.length;
  const totalPages = Math.max(1, Math.ceil(currentDataCount / pageSize));
  const hasData = currentDataCount > 0;
  const loading = !contract;

  return (
    <>
      <div>
        <h2>{texts.title}</h2>
        <div className="relative left-1/2 mt-4 flex w-screen -translate-x-1/2 justify-end pr-6">
          <Button onClick={() => contract?.property?.id && contract?.id && openModal(contract.property.id, { contractId: contract.id })}>
            + {texts.createButton}
          </Button>
        </div>
      </div>
      <div className="py-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-end">
          <NormalFormSelectionField<IncidentCategoryOption>
            label={texts.filters.type}
            name="category"
            options={categoryOptions}
            value={filters.categoryOption}
            onChange={(value) => handleFilterChange('categoryOption', value)}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
          <NormalFormSelectionField<OrderDirectionOption>
            label={texts.filters.order}
            name="orderDirection"
            options={orderDirectionOptions}
            value={filters.orderDirection}
            onChange={(value) => handleFilterChange('orderDirection', value)}
          />
          <div className="flex items-end">
            <Button secondary onClick={() => {
              setFilters(INITIAL_FILTERS);
              setCurrentPage(1);
            }}>
              {texts.clear || (userLanguage === 'es' ? 'Limpiar filtros' : 'Clear filters')}
            </Button>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-6">
          <LoadingSpinner text />
        </div>
      ) : hasData ? (
        <>
          <div className="grid grid-cols-[1.5fr_1.5fr_2fr_1fr_1fr] rounded-xl py-6">
            <div className="contents font-bold text-left">
              <div className="border-y border-gray-400 p-[15px]">{texts.tableHeader.category}</div>
              <div className="border-y border-gray-400 p-[15px]">{texts.tableHeader.title}</div>
              <div className="border-y border-gray-400 p-[15px]">{texts.tableHeader.description}</div>
              <div className="border-y border-gray-400 p-[15px]">{texts.tableHeader.date}</div>
              <div className="border-y border-gray-400 p-[15px]">{texts.tableHeader.amount}</div>
            </div>
            {flattenedData.map((expense, index) => (
              <div key={expense.id} className="contents">
                <div className={`place-content-center self-stretch border-b border-gray-300 px-[15px] py-4 text-sm ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>{expense.category}</div>
                <div className={`place-content-center self-stretch border-b border-gray-300 px-[15px] py-4 text-sm ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>{expense.title}</div>
                <div className={`place-content-center self-stretch border-b border-gray-300 px-[15px] py-4 text-sm ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>{expense.description}</div>
                <div className={`place-content-center self-stretch border-b border-gray-300 px-[15px] py-4 text-sm ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>{readableDate(expense.date, userLanguage)}</div>
                <div className={`place-content-center self-stretch border-b border-gray-300 px-[15px] py-4 text-sm ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>{expense.amount}</div>
              </div>
            ))}
          </div>
          <Pagination
            currentPage={currentPage}
            onPageChange={handlePageChange}
            currentDataCount={currentDataCount}
            pageSize={PageSizeOption.TwentyFive}
            totalPages={totalPages}
          />
        </>
      ) : (
        <p>
          <b>{texts.noData}</b>
        </p>
      )}
    </>
  );
};

